import React, {useCallback, useMemo} from 'react';

import DropDownTree from '@pb/kit-component-dropdown-tree';

import {UserDropDownItem} from './user-drop-down-item';

import {usersDropDown} from './users-drop-down.module.styl';

import type {ChangeTreeType, DropDownItemRendererDataType} from '@pb/kit-component-dropdown-tree';
import type {UserSearchResult} from 'src/openapi/admin/models/UserSearchResult';

type Props = {
    users: Array<UserSearchResult>,
    checkedIds: Array<number>,
    disabled: boolean,
    onChange: (ids: Array<number>) => void,
    onSearch: (query: string) => void,
};

const parseUsers = (users: Array<UserSearchResult>) => {
    return users.map(({id, firstName, lastName}) => ({
        id: `${id}`,
        caption: [firstName, lastName].join(' ').trim(),
    }));
};

export const UsersDropDown = ({users, checkedIds, disabled, onChange, onSearch}: Props) => {
    const checkedIdsStr = useMemo(() => checkedIds.map(String), [checkedIds]);

    const treeData = useMemo(() => parseUsers(users), [users]);

    const usersById = useMemo(
        () => users.reduce((acc, user) => {
            acc[String(user.id)] = user;
            return acc;
        }, {} as Record<string, UserSearchResult>),
        [users],
    );

    const itemRenderer = useCallback(
        ({checkboxProps, data}: DropDownItemRendererDataType) => (
            <UserDropDownItem
                checkboxProps={checkboxProps}
                data={data}
                user={usersById[data.id]}
            />
        ),
        [usersById],
    );

    const onTreeChange = useCallback(
        ({checkedIdsList}: ChangeTreeType) => {
            onChange(checkedIdsList.map(Number));
        },
        [onChange],
    );

    return (
        <div className={usersDropDown}>
            <DropDownTree
                onSearch={onSearch}
                data={treeData}
                checkedIdsList={checkedIdsStr}
                inputProps={{placeholder: 'Select Users', disabled}}
                onTreeChange={onTreeChange}
                dropDownItemRenderer={itemRenderer}
            />
        </div>
    );
};
